import { TEnumName, useEnum } from "@/api/services/global/enum";
import { background, blue, button, divider, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import { TOption, TRequiredOne } from "@/types/common";
import { convertFullWidthToHalfWidth } from "@/utils/pieces";
import { Autocomplete, Box, Chip, CircularProgress, FilterOptionsState, FormHelperText, MenuItem, TextField, css, styled } from "@mui/material";
import { FC, SyntheticEvent, useMemo } from "react";

export type TComboboxMultiProps = {
  helperText?: string;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  limitTags?: number;
  value?: string[];
  onChange?: (value: string[]) => void;
} & TRequiredOne<{ options: TOption[]; enumName: TEnumName }>;

export const ComboboxMulti: FC<TComboboxMultiProps> = ({
  placeholder = "選択してください",
  helperText,
  options,
  enumName,
  loading = false,
  disabled,
  limitTags = 2,
  ...rest
}) => {
  const { data, isLoading } = useEnum(enumName);
  // Runtime guard: coerce legacy scalar values (from old saved conditions) into arrays
  // to prevent .map() crash during the scalar→array migration window.
  const rawValue = rest.value;
  const value: string[] = Array.isArray(rawValue) ? rawValue : rawValue == null || rawValue === "" ? [] : [String(rawValue)];

  const selectOptions = useMemo(() => options ?? data ?? [], [options, data]);

  // Map string[] values to TOption[] for Autocomplete
  const selectedOptions = useMemo(
    () => value.map((v) => selectOptions.find((opt) => opt.value === v)).filter(Boolean) as TOption[],
    [selectOptions, value],
  );

  const onChange = (_e: SyntheticEvent<unknown>, newValue: unknown) => {
    const newValues = (newValue as TOption[]).map((opt) => opt.value as string);
    rest.onChange?.(newValues);
  };

  return (
    <BoxWrapper position="relative">
      <StyledAutocomplete
        multiple
        limitTags={limitTags}
        noOptionsText="選択肢なし"
        disabled={disabled}
        value={selectedOptions}
        onChange={onChange}
        filterOptions={(options: unknown[], state: FilterOptionsState<unknown>) => {
          if (state.inputValue === "") return options;
          return options.filter((opt) => {
            const label = convertFullWidthToHalfWidth((opt as TOption).label);
            const query = convertFullWidthToHalfWidth(state.inputValue);
            return label.includes(query) || label.toLowerCase().includes(query.toLowerCase());
          });
        }}
        isOptionEqualToValue={(option, val) => (option as TOption).value === (val as TOption).value}
        getOptionLabel={(option) => (option as TOption).label}
        renderInput={(params) => <TextField {...params} placeholder={selectedOptions.length === 0 ? placeholder : ""} />}
        options={selectOptions}
        renderOption={({ key, ...props }, option) => {
          const opt = option as TOption;
          const isDisable = "hidden" in opt && opt.hidden === true;
          const isSelected = value.includes(opt.value as string);
          const isHidden = isDisable && !isSelected;
          if (isHidden) return null;
          return (
            <MenuItem {...props} key={key} value={opt.value} disabled={isDisable} title={opt.label}>
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{opt.label}</div>
            </MenuItem>
          );
        }}
        renderTags={(tagValues, getTagProps) =>
          (tagValues as TOption[]).map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip key={key} label={option.label} size="small" disabled={tagProps.disabled} onDelete={tagProps.onDelete} style={{ maxWidth: 120 }} />
            );
          })
        }
        componentsProps={{
          paper: {
            sx: {
              fontSize: "14px !important",
              ".MuiMenuItem-root": typography.body14,
            },
          },
        }}
      />
      {(isLoading || loading) && (
        <LoadingWrap>
          <CircularProgress color="inherit" size={14} />
        </LoadingWrap>
      )}
      {Boolean(helperText) && <FormHelperText>{helperText}</FormHelperText>}
    </BoxWrapper>
  );
};

const BoxWrapper = styled(Box)``;

const StyledAutocomplete = styled(Autocomplete)`
  width: 100%;
  .MuiAutocomplete-inputRoot {
    padding: 2px 56px 2px 4px !important;
    min-height: 34px;
    flex-wrap: nowrap;
    overflow: hidden;
    gap: 2px;
  }
  .MuiAutocomplete-inputRoot.Mui-focused {
    flex-wrap: wrap;
    overflow: visible;
  }

  /* "+N" limit tag indicator */
  .MuiAutocomplete-tag[class*="limitTag"] {
    font-size: 12px;
    max-width: none;
  }

  .MuiInputBase-root {
    position: relative;
    background: ${background.white};
    .MuiSelect-select {
      color: ${text.primary};
    }

    input::placeholder {
      color: ${text.primary};
    }
    fieldset {
      border-color: ${divider.middle};
      border-radius: ${rounded.xs};
      border-width: 1px !important;
    }
    &:hover,
    &.Mui-focused {
      fieldset {
        border-color: ${blue[70]};
      }
    }
    &.Mui-focused {
      &::after {
        content: "";
        position: absolute;
        top: -4px;
        left: -4px;
        width: 100%;
        height: 100%;
        padding: 2px;
        border: 2px solid ${button.secondary};
        box-sizing: content-box;
        border-radius: ${rounded.sm};
        z-index: -1;
      }
    }

    .MuiInputBase-input {
      padding: 4px 8px;
      ${css(typography.body14)};
      min-width: 30px !important;
    }

    &.Mui-disabled {
      background: ${background.disable};
      fieldset {
        border-color: ${divider.middle}!important;
      }
    }
  }

  /* Chip: maxWidth is set via inline sx prop for ellipsis to work */
`;

const LoadingWrap = styled(Box)`
  position: absolute;
  top: 0;
  right: 0;
  background-color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 30px;
`;
